
import { Vue, Component, Prop, Emit } from 'vue-property-decorator';
import PortionSelector from '@/components/PortionSelector.vue';
import { ProductInput } from '@/types';
import {MutationType, Weekday, Weekend} from '@/enums';
import {getDanishDay, t} from '@/utils';

@Component({
	components: {
		PortionSelector
	}
})
export default class ProductField extends Vue {
	@Prop() input!: ProductInput;
	private selectedDays: string[] = [];
	private hasSendNotification: boolean = false;
	private showPortionSelector: boolean = false;
	private defaultPortionWeight: number = 30;

	private inputUnit: string = this.input.data.unit || 'g';
	private inputMaxWeight: number = this.input.data.maxUnitQuantities || 160;

	private get days(): string[] {
			return Object.values(Weekday);
	}

	private get anyPortionSelected(): boolean {
		for (const day of this.days) {
			//@ts-ignore
			if (this.input.selectedPortions[day].index >= 0) {
				return true;
			}
		}

		return false;
	}

	private get totals(): { [key in Weekday]: number } {
		const totals: { [key in Weekday]: number } = {
			[Weekday.MONDAY]: 0,
			[Weekday.TUESDAY]: 0,
			[Weekday.WEDNESDAY]: 0,
			[Weekday.THURSDAY]: 0,
			[Weekday.FRIDAY]: 0,
			[Weekday.SATURDAY]: 0,
			[Weekday.SUNDAY]: 0
		};

		for (const day of this.days) {
			const weekday = <Weekday>day;

			if (this.input.selectedPortions[weekday].index < 0) {
				continue;
			}

			const portionIndex: number = this.input.selectedPortions[weekday].index;
			const portionWeight: number = this.input.data.portions[portionIndex]?.weight || this.defaultPortionWeight;

			const multiplier = this.input.selectedPortions[weekday].multiplier;

			totals[weekday] = portionWeight * multiplier;
		}

		return totals;
	}

	private get allTotalsZero(): boolean {

		for (const day in this.totals) {
			if (this.totals[day as Weekday] > 0) {
				return false;
			}
		}

		return true;
	}

	private totalByDay(day: string): number {
		const weekday = <Weekday>day;
		return this.totals[weekday];
	}

	private totalByDayString(day: string): string {
		const totalByDay = this.totalByDay(day);

		if (totalByDay == 0) {
			return '';
		}

		const unit: string = this.inputUnit;
		return totalByDay + unit;
	}

	@Emit()
	private remove(): void {}

	private openPortionSelector(selectedDays: string[]): void {
		this.selectedDays = selectedDays;
		this.showPortionSelector = true;
	}

	private onPortionSelected(selectedPortion: number): void {
		for (const day of this.selectedDays) {
			this.input.selectedPortions[<Weekday>day].index = selectedPortion;
		}

		this.checkIfShouldSendWarning([...this.selectedDays]);

		this.closePortionSelector();

		this.$store.commit(MutationType.SAVE_INPUT_TO_LOCAL);
	}

	private closePortionSelector(): void {
		this.selectedDays = [];
		this.showPortionSelector = false;
	}

	private subtractPortion(day: string): void {
		const weekday = <Weekday>day;
		const multiplier = this.input.selectedPortions[weekday].multiplier;

		const amountToSubtract = 1;
		const newMultiplier = multiplier - amountToSubtract;

		if (newMultiplier > 0) {
			this.input.selectedPortions[weekday].multiplier = newMultiplier;
			return;
		}

		// Set chosen portion to nothing
		this.input.selectedPortions[weekday].index = -1;
	}

	private addPortion(day: string): void {
		const weekday = <Weekday>day;
		const multiplier = this.input.selectedPortions[weekday].multiplier;

		const amountToAdd = 1;

		const newMultiplier = multiplier + amountToAdd;
		this.input.selectedPortions[weekday].multiplier = newMultiplier;

		this.checkIfShouldSendWarning([day]);
	}

	private checkIfShouldSendWarning(days: string[]): void {
		if (this.hasSendNotification) {
			return;
		}

		for (const day of days) {
			const weekday = <Weekday>day;

			const dayTotal = this.totalByDay(day);

			if (dayTotal > this.inputMaxWeight) {
				this.sendMaxPortionWarning();
				return;
			}
		}
	}

	private getPortionLabel(index: number): string {
		if (index < 0) {
			//@ts-ignore
			return this.$t('portion', 'none');
		}
		return this.input.data.portions[index].label;
	}

	private sendMaxPortionWarning():void {
		this.hasSendNotification = true;

		// timeout to ensure that the number has shifted before the warning
		setTimeout(() => {
			// continue pop up
			let confirmQuestion = t('portion', 'maxPortionWarning');

			alert(confirmQuestion)

		}, 50);
		return;
	}

	private translateDay(day: Weekday | Weekend): string {
		return getDanishDay(day);
	}
}
